import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Button, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { MemberService, type CreditLedgerEntry, type CustomerCredit } from '../../services/member/memberService.ts';

const memberService = new MemberService();

const euro = (n: number) => `€${Number(n ?? 0).toFixed(2)}`;

const headCellSx = {
    paddingLeft: 'var(--space-24)',
    py: 'var(--space-16)',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
};

const bodyCellSx = {
    py: 'var(--space-16)',
    paddingLeft: 'var(--space-24)',
    fontSize: 'var(--text-xs)',
};

/**
 * A customer's Vello Credit on their own page: what they hold, how they got
 * every euro of it, and a way to give or take some by hand.
 *
 * The ledger is the point. A balance on its own invites the question this
 * panel exists to answer - "where did that come from?" - so each row names
 * the order it came from, or the note and the admin behind an adjustment.
 */
const CustomerCreditPanel: React.FC<{ customerId: number }> = ({ customerId }) => {
    const { t } = useTranslation();
    const notify = useNotify();

    const [credit, setCredit] = useState<CustomerCredit | null>(null);
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;

        memberService.getCustomerCredit(customerId)
            .then((data) => { if (!cancelled) setCredit(data); })
            .catch((e) => {
                Sentry.captureException(e);
                if (!cancelled) notify(t('members.credit.loadFailed', 'Could not load this customer\'s credit'), { type: 'error' });
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [customerId, notify, t]);

    const submit = useCallback(async (sign: 1 | -1) => {
        const value = Number(amount);
        if (!Number.isFinite(value) || value <= 0) {
            notify(t('members.credit.amountRequired', 'Enter an amount greater than zero'), { type: 'warning' });
            return;
        }
        if (!note.trim()) {
            notify(t('members.credit.noteRequired', 'Say why — it stays on the ledger'), { type: 'warning' });
            return;
        }

        setSaving(true);
        try {
            const updated = await memberService.grantCustomerCredit({
                customerId,
                amount: Number((value * sign).toFixed(2)),
                note: note.trim()
            });
            setCredit(updated);
            setAmount('');
            setNote('');
            notify(
                sign > 0
                    ? t('members.credit.given', 'Credit added. The customer has been notified.')
                    : t('members.credit.taken', 'Credit removed.'),
                { type: 'success' }
            );
        } catch (e: any) {
            const message = e?.response?.data?.error
                ?? e?.response?.data?.errors?.amount
                ?? t('common.saveFailed', 'Save failed');
            notify(message, { type: 'error' });
            Sentry.captureException(e);
        } finally {
            setSaving(false);
        }
    }, [amount, note, customerId, notify, t]);

    const label = (entry: CreditLedgerEntry) => t(`members.credit.types.${entry.type}`, entry.type);

    // What the row is about, in the order someone reading it would want it:
    // the note for an adjustment, the order for anything an order caused, the
    // other person for a referral.
    const detail = (entry: CreditLedgerEntry) => {
        const parts: string[] = [];
        if (entry.note) parts.push(entry.note);
        if (entry.orderNumber) parts.push(`#${entry.orderNumber}`);
        if (entry.relatedCustomerName) parts.push(entry.relatedCustomerName);
        if (entry.grantedByName) parts.push(t('members.credit.by', 'by {{name}}', { name: entry.grantedByName }));
        return parts.join(' · ');
    };

    if (loading) {
        return <Box sx={{ py: 4, pl: 'var(--space-24)' }}><CircularProgress size={24} /></Box>;
    }

    if (!credit) return null;

    return (
        <Box sx={{ mt: 6 }}>
            <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2, pl: 'var(--space-24)' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 'var(--text-xxl)' }}>
                    {t('members.credit.title', 'Vello Credit')}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--color-primary)', fontVariantNumeric: 'tabular-nums' }}>
                    {euro(credit.balance)}
                </Typography>
            </Stack>

            <Box sx={{ border: '1px solid var(--color-secondary)', borderRadius: 'var(--radius-6)', overflow: 'hidden', width: '100%' }}>
                <Box sx={{ p: 'var(--space-24)', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
                    <TextField
                        size="small"
                        type="number"
                        label={t('members.credit.amount', 'Amount')}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        slotProps={{
                            htmlInput: { step: 1, min: 0, max: 500 },
                            input: { startAdornment: <InputAdornment position="start">€</InputAdornment> }
                        }}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        size="small"
                        label={t('members.credit.note', 'Reason')}
                        placeholder={t('members.credit.notePlaceholder', 'Late delivery, goodwill…')}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        slotProps={{ htmlInput: { maxLength: 255 } }}
                        sx={{ flex: 1, minWidth: 260 }}
                    />
                    <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
                        <Button
                            variant="contained"
                            disabled={saving}
                            onClick={() => submit(1)}
                            label={t('members.credit.give', 'Give credit')}
                        />
                        <Button
                            variant="outlined"
                            disabled={saving}
                            onClick={() => submit(-1)}
                            label={t('members.credit.take', 'Take back')}
                        />
                    </Stack>
                </Box>

                {credit.entries.length === 0 ? (
                    <Typography sx={{ px: 'var(--space-24)', pb: 'var(--space-24)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                        {t('members.credit.empty', 'No credit yet. This customer has not earned or been given any.')}
                    </Typography>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCellSx}>{t('members.credit.columns.date', 'Date')}</TableCell>
                                <TableCell sx={headCellSx}>{t('members.credit.columns.what', 'What')}</TableCell>
                                <TableCell sx={headCellSx}>{t('members.credit.columns.detail', 'Detail')}</TableCell>
                                <TableCell sx={headCellSx} align="right">{t('members.credit.columns.amount', 'Amount')}</TableCell>
                                <TableCell sx={headCellSx} align="right">{t('members.credit.columns.left', 'Left')}</TableCell>
                                <TableCell sx={headCellSx}>{t('members.credit.columns.expires', 'Expires')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {credit.entries.map((entry) => (
                                <TableRow key={entry.id}>
                                    <TableCell sx={bodyCellSx}>{new Date(entry.createdAt).toLocaleDateString()}</TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <span>{label(entry)}</span>
                                            {entry.type === 'ADJUSTMENT' && (
                                                <Chip size="small" label={t('members.credit.manual', 'manual')} />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: 'var(--color-text-secondary)' }}>{detail(entry)}</TableCell>
                                    <TableCell
                                        sx={{
                                            ...bodyCellSx,
                                            fontWeight: 700,
                                            fontVariantNumeric: 'tabular-nums',
                                            color: entry.amount >= 0 ? 'var(--color-primary)' : 'var(--color-error)'
                                        }}
                                        align="right"
                                    >
                                        {entry.amount >= 0 ? '+' : '−'}{euro(Math.abs(entry.amount))}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }} align="right">
                                        {entry.remaining !== 0 ? euro(entry.remaining) : '—'}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: entry.expired ? 'var(--color-error)' : undefined }}>
                                        {entry.expiresAt
                                            ? `${new Date(entry.expiresAt).toLocaleDateString()}${entry.expired ? ` · ${t('members.credit.expiredTag', 'expired')}` : ''}`
                                            : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </Box>
    );
};

export default CustomerCreditPanel;
